import React from "react";
import {Container} from "react-bootstrap";
import {LOADER_SIZE} from "constants/StyleConstants.js";
import {ws} from "styles/tokens";
import AppNav, {APP_NAV_HEIGHT} from "components/Common/AppNav";
import BottomBar from "components/Common/BottomBar";
import {StyleDiv, StyleDivCenterChild} from "components/Common/StyleDiv";
import {useLayout} from "contexts/LayoutContext";
import {isAndroid} from "FirebaseUtils";
import AndroidNavigationBar from "components/Common/AndroidNavigationBar";
import ActivityPageSkeleton from "components/Activity/ActivityPageSkeleton";
import EquipmentsSkeleton from "components/Equipments/EquipmentsSkeleton";
import ClipLoader from "react-spinners/ClipLoader";
import {useHistory, useLocation} from "react-router-dom";
import WaitingModal from "components/Common/WaitingModal";
import {PAGE_URLS} from "constants/AppConstants";

const AppNavAny: any = AppNav;
const WaitingModalAny: any = WaitingModal;

interface Props {
  children?: React.ReactNode;
  isNavDisabled?: boolean;
}

function Layout({children, isNavDisabled}: Props) {
  const {showLoader, showWaitingModal, waitingModalText} = useLayout();
  const history = useHistory();
  const location = useLocation();

  // Check if current route is a webview route
  // Check pathname for /webview or check if URL has token param (indicates webview authentication)
  const isWebview =
    location.pathname.includes("/webview") ||
    location.pathname.includes(`${PAGE_URLS.activityPageUrl}/webview`) ||
    (location.pathname.includes(PAGE_URLS.activityPageUrl) && new URLSearchParams(location.search).has("tokenLogin"));

  const isActivityRoute =
    location.pathname === PAGE_URLS.activityPageUrl || location.pathname.startsWith(`${PAGE_URLS.activityPageUrl}/`);
  const isRootRoute = location.pathname === "/";
  const isDiaryRoute =
    location.pathname === PAGE_URLS.diaryPageUrl ||
    location.pathname === PAGE_URLS.oldDiaryPageUrl ||
    (!isAndroid && isRootRoute);
  const isExploreRoute =
    location.pathname.startsWith(PAGE_URLS.aroundMePageUrl) ||
    location.pathname.startsWith(PAGE_URLS.publicAroundMePageUrl) ||
    (isAndroid && isRootRoute);
  const isEquipmentRoute = location.pathname.includes(PAGE_URLS.equipmentsPageUrl);

  const renderLoader = () => {
    if (isExploreRoute) {
      return <ClipLoader loading={true} size={LOADER_SIZE} color={ws.accent} />;
    }
    if (isEquipmentRoute) {
      return <EquipmentsSkeleton />;
    }
    if (isDiaryRoute) {
      return <ActivityPageSkeleton />;
    }
    if (isActivityRoute) {
      return <ActivityPageSkeleton isWebview={isWebview} />;
    }
    return <ActivityPageSkeleton />;
  };

  return (
    <Container fluid>
      {!isAndroid && !isWebview && <AppNavAny isNavDisabled={isNavDisabled} />}

      <StyleDiv
        backgroundColor={ws.wash}
        color={ws.fg}
        padding={`${isAndroid ? "0px" : "20px"} 0px`}
        maxWidth={isAndroid ? "110vw" : "250vw"}
        height={isAndroid ? "100vh" : isWebview ? "100vh" : `calc(100vh - ${APP_NAV_HEIGHT})`}
        position="relative"
        overflow="hidden"
      >
        <StyleDiv display={showLoader ? "block" : "none"} height="100%" margin={showLoader ? "0px" : "0 auto"}>
          <AndroidNavigationBar
            onBackClick={() => history.goBack()}
            title="Loading..."
            titleStyle={{fontWeight: "bold"}}
          />
          <StyleDivCenterChild width="100%" height="100%">
            {isAndroid && <div style={{width: "100%", height: "75px"}} />}
            {renderLoader()}
          </StyleDivCenterChild>
        </StyleDiv>

        <StyleDiv display={showLoader ? "none" : "block"} height="100%" overflow="hidden auto">
          {children}
        </StyleDiv>
      </StyleDiv>

      <WaitingModalAny show={showWaitingModal} height={"10px"} text={waitingModalText} />
      {isAndroid && !isWebview && <BottomBar isNavDisabled={isNavDisabled} />}
    </Container>
  );
}

export default Layout;
